// modalManager.js - Centralized modal lifecycle management

import { log } from '../utils/logging.js';

// ============================================================================
// Modal Registry
// ============================================================================

class ModalRegistry {
  constructor() {
    this.configs = new Map();
    this.instances = new Map();
    this.cleanupHandlers = new Map();
    this.initializedKeys = new Set();
  }

  register(sceneName, modalId, config) {
    const key = this._createKey(sceneName, modalId);
    this.configs.set(key, {
      sceneName,
      modalId,
      ...config
    });
  }

  getConfig(sceneName, modalId) {
    const key = this._createKey(sceneName, modalId);
    return this.configs.get(key);
  }

  getAllConfigsForScene(sceneName) {
    const configs = [];
    for (const [key, config] of this.configs.entries()) {
      if (config.sceneName === sceneName) {
        configs.push({ key, config });
      }
    }
    return configs;
  }

  setInstance(sceneName, modalId, instance) {
    const key = this._createKey(sceneName, modalId);
    this.instances.set(key, instance);
  }

  getInstance(sceneName, modalId) {
    const key = this._createKey(sceneName, modalId);
    return this.instances.get(key);
  }

  setCleanup(sceneName, modalId, cleanup) {
    const key = this._createKey(sceneName, modalId);
    this.cleanupHandlers.set(key, cleanup);
  }

  getCleanup(sceneName, modalId) {
    const key = this._createKey(sceneName, modalId);
    return this.cleanupHandlers.get(key);
  }

  markInitialized(sceneName, modalId) {
    const key = this._createKey(sceneName, modalId);
    this.initializedKeys.add(key);
  }

  isInitialized(sceneName, modalId) {
    const key = this._createKey(sceneName, modalId);
    return this.initializedKeys.has(key);
  }

  clearScene(sceneName) {
    const keysToDelete = [];

    for (const [key, config] of this.configs.entries()) {
      if (config.sceneName === sceneName) {
        keysToDelete.push(key);
      }
    }

    for (const key of keysToDelete) {
      this.instances.delete(key);
      this.cleanupHandlers.delete(key);
      this.initializedKeys.delete(key);
    }
  }

  _createKey(sceneName, modalId) {
    return `${sceneName}:${modalId}`;
  }
}

// ============================================================================
// Modal Instance
// ============================================================================

class ModalInstance {
  constructor(element, config, closeCallback) {
    this.element = element;
    this.config = config;
    this.closeCallback = closeCallback;
    this.isOpen = false;
    this.eventCleanups = [];
  }

  open() {
    // Update content if needed
    this.config.updateContent?.();

    // Custom open handler
    const shouldOpen = this.config.openHandler?.() ?? true;
    if (!shouldOpen) return false;

    // Show modal
    this.element.style.display = 'flex';
    this.element.offsetHeight; // Force reflow
    this.element.classList.add('open');
    this.isOpen = true;

    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');

    return true;
  }

  close() {
    if (!this.isOpen) return;

    this.element.classList.remove('open');
    this.isOpen = false;

    // Delay hiding to allow close animation
    setTimeout(() => {
      if (!this.isOpen) {
        this.element.style.display = 'none';
      }
    }, 250);
  }

  cleanup() {
    // Run all event cleanups
    for (const cleanup of this.eventCleanups) {
      cleanup();
    }
    this.eventCleanups = [];
  }

  addEventCleanup(cleanup) {
    this.eventCleanups.push(cleanup);
  }
}

// ============================================================================
// Modal Initializer
// ============================================================================

class ModalInitializer {
  async initialize(config, registry) {
    const modalElement = document.getElementById(config.modalId);

    if (!modalElement) {
      console.warn(`[modalManager] Modal element not found: ${config.modalId}`);
      return null;
    }

    const key = `${config.sceneName}:${config.modalId}`;
    log(`[modalManager] Initializing: ${key}`);

    try {
      // Create modal instance
      const instance = new ModalInstance(modalElement, config, () =>
        registry.getInstance(config.sceneName, config.modalId)?.close()
      );

      // Run custom initialization
      const customCleanup = await config.initialize?.(modalElement);

      // Setup close button
      this._setupCloseButton(modalElement, config, instance);

      // Setup overlay click
      this._setupOverlayClick(modalElement, instance);

      // Store cleanup
      const cleanup = () => {
        instance.cleanup();
        customCleanup?.();
      };
      registry.setCleanup(config.sceneName, config.modalId, cleanup);

      // Store instance
      registry.setInstance(config.sceneName, config.modalId, instance);
      registry.markInitialized(config.sceneName, config.modalId);

      log(`✓ [modalManager] Initialized: ${key}`);
      return instance;
    } catch (err) {
      console.error(`[modalManager] Failed to initialize ${key}:`, err);
      return null;
    }
  }

  _setupCloseButton(modalElement, config, instance) {
    const closeBtn = modalElement.querySelector(
      `[id$="-close"], .modal-close, .${config.modalId.replace('-modal', '')}-close`
    );

    if (!closeBtn) return;

    const closeHandler = () => instance.close();
    closeBtn.addEventListener('click', closeHandler);

    instance.addEventCleanup(() => {
      closeBtn.removeEventListener('click', closeHandler);
    });
  }

  _setupOverlayClick(modalElement, instance) {
    const overlayHandler = e => {
      if (e.target === modalElement) {
        instance.close();
      }
    };

    modalElement.addEventListener('click', overlayHandler);

    instance.addEventCleanup(() => {
      modalElement.removeEventListener('click', overlayHandler);
    });
  }
}

// ============================================================================
// Modal Manager
// ============================================================================

class ModalManager {
  constructor() {
    this.registry = new ModalRegistry();
    this.initializer = new ModalInitializer();
    this.currentScene = null;

    this._setupGlobalListeners();
  }

  // ------------------------------------------------------------------------
  // Public API
  // ------------------------------------------------------------------------

  registerModal(sceneName, modalId, config) {
    this.registry.register(sceneName, modalId, config);
  }

  async openModal(sceneName, modalId) {
    const instance = this.registry.getInstance(sceneName, modalId);

    if (!instance) {
      console.warn(`[modalManager] Modal not initialized: ${sceneName}:${modalId}`);
      return false;
    }

    // Close other modals first
    this.closeAllModals();

    const opened = instance.open();

    if (opened) {
      log(`[modalManager] Opened: ${sceneName}:${modalId}`);
    }

    return opened;
  }

  closeModal(sceneName, modalId) {
    const instance = this.registry.getInstance(sceneName, modalId);

    if (!instance || !instance.isOpen) return;

    instance.close();

    // Restore body scroll if no modals are open
    if (!this._hasOpenModals()) {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    }

    log(`[modalManager] Closed: ${sceneName}:${modalId}`);
  }

  closeTopModal() {
    for (const instance of this.registry.instances.values()) {
      if (instance.isOpen) {
        instance.close();

        if (!this._hasOpenModals()) {
          document.body.style.overflow = '';
          document.body.classList.remove('modal-open');
        }
        break;
      }
    }
  }

  closeAllModals() {
    let hadOpenModals = false;

    for (const instance of this.registry.instances.values()) {
      if (instance.isOpen) {
        instance.close();
        hadOpenModals = true;
      }
    }

    if (hadOpenModals) {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    }
  }

  async initializeSceneModals(sceneName) {
    log(`[modalManager] Initializing modals for ${sceneName}`);

    const configs = this.registry.getAllConfigsForScene(sceneName);

    for (const { key, config } of configs) {
      if (!this.registry.isInitialized(config.sceneName, config.modalId)) {
        await this.initializer.initialize(config, this.registry);
      }
    }
  }

  cleanupCurrentScene() {
    if (!this.currentScene) return;

    log(`[modalManager] Cleaning up scene: ${this.currentScene}`);

    this.closeAllModals();

    // Run cleanup handlers
    const configs = this.registry.getAllConfigsForScene(this.currentScene);
    for (const { config } of configs) {
      const cleanup = this.registry.getCleanup(config.sceneName, config.modalId);
      cleanup?.();
    }

    // Clear registry
    this.registry.clearScene(this.currentScene);

    this.currentScene = null;
  }

  // ------------------------------------------------------------------------
  // Private Methods
  // ------------------------------------------------------------------------

  _setupGlobalListeners() {
    // Close all modals when scene changes
    window.addEventListener('scene-loading', () => {
      log('[modalManager] scene-loading event - closing all modals');
      this.closeAllModals();
    });

    // Initialize modals when ready
    window.addEventListener('modals-ready', event => {
      const { sceneName, fromDirectLoad } = event.detail;
      log(`[modalManager] modals-ready for ${sceneName} (direct: ${fromDirectLoad})`);
      this.initializeSceneModals(sceneName);
    });

    // ESC key to close modals
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        this.closeTopModal();
      }
    });
  }

  _hasOpenModals() {
    for (const instance of this.registry.instances.values()) {
      if (instance.isOpen) return true;
    }
    return false;
  }
}

// ============================================================================
// Exports
// ============================================================================

export const modalManager = new ModalManager();

export function openModal(sceneName, modalId) {
  return modalManager.openModal(sceneName, modalId);
}
